import React from 'react'
import { XMarkIcon } from '@heroicons/react/24/outline'
import { Heading, Text } from '../../components/ui'
import type { FormField, FormResponseRelatorioRow } from '../../types/forms'

type ContatosPortalFormularioRespostaDetalheModalProps = {
  row: FormResponseRelatorioRow | null
  fields: FormField[]
  formatValue: (value: unknown, field?: FormField) => string
  formatDateTime: (iso: string) => string
  onClose: () => void
}

function isWideFormField(field: FormField): boolean {
  return field.tipo === 'textarea' || field.tipo === 'text' || field.tipo === 'checkbox'
}

const ContatosPortalFormularioRespostaDetalheModal: React.FC<
  ContatosPortalFormularioRespostaDetalheModalProps
> = ({ row, fields, formatValue, formatDateTime, onClose }) => {
  if (!row) return null

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-screen items-center justify-center p-4">
        <div
          className="fixed inset-0 bg-black/50 transition-opacity"
          onClick={onClose}
          aria-hidden
        />
        <div
          className="relative bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-3xl w-full max-h-[85vh] flex flex-col"
          onClick={(e) => e.stopPropagation()}
          role="dialog"
          aria-modal="true"
          aria-labelledby="formulario-resposta-detalhe-titulo"
        >
          <div className="flex-shrink-0 border-b border-gray-200 dark:border-gray-700 px-6 py-4 flex items-start justify-between gap-4">
            <div className="min-w-0">
              <Heading
                as="h2"
                size="2xl"
                id="formulario-resposta-detalhe-titulo"
                className="text-gray-900 dark:text-white"
              >
                Detalhes da resposta
              </Heading>
              <Text size="sm" color="muted" className="mt-1">
                {formatDateTime(row.created_at)}
                {row.form_name ? ` · ${row.form_name}` : ''}
              </Text>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
              aria-label="Fechar"
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>

          <div className="flex-1 overflow-y-auto px-6 py-5 space-y-5">
            <div className="flex flex-wrap items-center gap-x-6 gap-y-3 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/40 p-4">
              <div className="min-w-0 flex-1">
                <Text size="xs" color="muted" className="uppercase tracking-wide">
                  Corretor
                </Text>
                <Text size="sm" className="text-gray-900 dark:text-white font-medium break-words">
                  {row.broker_name}
                  {row.broker_id_scgp != null ? (
                    <span className="ml-2 text-xs font-normal text-gray-500 dark:text-gray-400">
                      SCGP {row.broker_id_scgp}
                    </span>
                  ) : null}
                </Text>
              </div>
              <div>
                <Text size="xs" color="muted" className="uppercase tracking-wide">
                  Versão
                </Text>
                <Text size="sm" className="text-gray-900 dark:text-white font-medium">
                  v{row.form_version}
                </Text>
              </div>
            </div>

            <dl className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4">
              {fields.map((field) => (
                <div
                  key={field.id}
                  className={isWideFormField(field) ? 'md:col-span-2' : undefined}
                >
                  <dt className="text-xs font-semibold text-gray-500 dark:text-gray-400">
                    {field.label}
                  </dt>
                  <dd className="mt-1 text-sm text-gray-900 dark:text-gray-100 whitespace-pre-wrap break-words">
                    {formatValue(row.responses[String(field.id)], field)}
                  </dd>
                </div>
              ))}
            </dl>
          </div>
        </div>
      </div>
    </div>
  )
}

export default ContatosPortalFormularioRespostaDetalheModal
